<template>
  <div class="apply-wrapper">
    <div class="apply-card">
      <template v-if="!sent">
        <h2 class="mb-1">{{ $t('influencer_apply.title') }}</h2>
        <p class="text-muted mb-3">{{ $t('influencer_apply.subtitle') }}</p>

        <b-form-group :label="$t('influencer.name')">
          <b-form-input v-model="model.name" type="text" :state="fieldState('name')"/>
        </b-form-group>

        <b-form-group :label="$t('influencer.email')">
          <b-form-input v-model="model.email" type="email" :state="fieldState('email')"/>
          <small class="text-muted">{{ $t('influencer_apply.email_hint') }}</small>
        </b-form-group>

        <div class="vx-row">
          <div class="vx-col sm:w-1/2 w-full">
            <b-form-group :label="$t('influencer.person_type')">
              <b-form-radio-group v-model="model.person_type" :options="personTypes"/>
            </b-form-group>
          </div>
          <div class="vx-col sm:w-1/2 w-full">
            <b-form-group :label="$t('influencer.document')">
              <b-form-input v-model="model.document" type="text"/>
            </b-form-group>
          </div>
        </div>

        <!--
          Auto-cadastro público cria conta na plataforma — logo vale a idade mínima
          (LGPD, Art. 14). O campo não existia aqui, e este era o caminho mais
          exposto: público, sem autenticação e sem verificação de idade nenhuma.
        -->
        <b-form-group label="Data de nascimento">
          <b-form-input v-model="model.birthdate" type="date"/>
          <small v-if="model.birthdate && !isOfAge" class="text-danger d-block">
            É necessário ter pelo menos {{ minAge }} anos.
          </small>
          <small v-else class="text-muted">
            Necessária para confirmar a idade mínima de {{ minAge }} anos.
          </small>
        </b-form-group>

        <b-form-group :label="$t('influencer.bio')">
          <b-form-textarea v-model="model.bio" rows="2" :placeholder="$t('influencer_apply.bio_placeholder')"/>
        </b-form-group>

        <div class="flex justify-between items-center mb-1">
          <label class="mb-0">{{ $t('influencer.networks') }}</label>
          <b-button size="sm" variant="outline-primary" @click="addProfile">
            <feather-icon icon="PlusIcon"/>
          </b-button>
        </div>
        <small class="text-muted d-block mb-2">{{ $t('influencer_apply.networks_hint') }}</small>

        <div v-for="(profile, index) in model.social_profiles" :key="index" class="vx-row items-end mb-1">
          <div class="vx-col sm:w-4/12 w-full">
            <b-form-select v-model="profile.network" :options="networks"/>
          </div>
          <div class="vx-col sm:w-5/12 w-full">
            <b-form-input v-model="profile.handle" type="text" placeholder="@seu.perfil"/>
          </div>
          <div class="vx-col sm:w-2/12 w-full">
            <b-form-input v-model="profile.followers" type="number" min="0" :placeholder="$t('influencer.followers')"/>
          </div>
          <div class="vx-col sm:w-1/12 w-full">
            <feather-icon
              v-if="model.social_profiles.length > 1"
              icon="Trash2Icon"
              class="h-5 w-5 stroke-current text-danger cursor-pointer"
              @click="model.social_profiles.splice(index, 1)"/>
          </div>
        </div>

        <b-form-checkbox v-model="model.terms_accepted" class="mt-3">
          {{ $t('influencer_apply.terms') }}
        </b-form-checkbox>

        <b-alert v-if="errorMessage" variant="danger" show class="mt-2 p-1">
          <small>{{ errorMessage }}</small>
        </b-alert>

        <b-button
          variant="primary"
          class="mt-3 w-full"
          :disabled="!canSubmit || isSending"
          @click="submit">
          {{ isSending ? $t('influencer_apply.sending') : $t('influencer_apply.submit') }}
        </b-button>

        <p class="text-muted text-center mt-2 mb-0">
          <small>{{ $t('influencer_apply.review_note') }}</small>
        </p>
      </template>

      <template v-else>
        <div class="text-center py-4">
          <feather-icon icon="CheckCircleIcon" class="h-12 w-12 text-success mb-2"/>
          <h3>{{ $t('influencer_apply.sent_title') }}</h3>
          <p class="text-muted mb-0">{{ sentMessage }}</p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  BFormGroup, BFormInput, BFormTextarea, BFormSelect, BFormRadioGroup,
  BFormCheckbox, BButton, BAlert
} from 'bootstrap-vue'
import axios from 'axios'
import { MIN_AGE } from '@core/utils/validations/validations'

/**
 * Auto-cadastro público de influencer.
 *
 * Não usa os serviços de `services/api` de propósito: todos herdam de `Rest`, que
 * anexa o token do usuário logado — e esta tela é anônima. Um `axios` direto no
 * endpoint público evita depender de um estado de sessão que aqui não existe.
 *
 * A candidatura nasce pendente e não dá acesso a nada: quem libera é a fila de
 * aprovação da equipe.
 */
export default {
  components: {
    BFormGroup, BFormInput, BFormTextarea, BFormSelect, BFormRadioGroup,
    BFormCheckbox, BButton, BAlert
  },
  data: () => ({
    isSending: false,
    sent: false,
    sentMessage: '',
    errorMessage: '',
    touched: false,
    model: {
      name: '',
      email: '',
      person_type: 'PF',
      document: '',
      birthdate: '',
      bio: '',
      terms_accepted: false,
      social_profiles: [{ network: 'IG', handle: '', followers: null }]
    },
    personTypes: [
      { text: 'Pessoa física', value: 'PF' },
      { text: 'Pessoa jurídica', value: 'PJ' }
    ],
    networks: [
      { text: 'Instagram', value: 'IG' },
      { text: 'TikTok', value: 'TT' },
      { text: 'YouTube', value: 'YT' },
      { text: 'Facebook', value: 'FB' },
      { text: 'X (Twitter)', value: 'TW' },
      { text: 'Kwai', value: 'KW' },
      { text: 'Outra', value: 'OT' }
    ]
  }),
  computed: {
    canSubmit() {
      return !!this.model.name
        && /.+@.+\..+/.test(this.model.email)
        && this.isOfAge
        && this.model.terms_accepted
        && this.model.social_profiles.some(p => (p.handle || '').trim().length > 0)
    },
    minAge() {
      return MIN_AGE
    },
    /** Idade em anos completos — o aniversário precisa ter passado. */
    isOfAge() {
      if (!this.model.birthdate) return false

      const birthDate = new Date(this.model.birthdate)
      if (isNaN(birthDate.getTime()) || birthDate > new Date()) return false

      const today = new Date()
      let age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age -= 1

      return age >= MIN_AGE
    }
  },
  methods: {
    fieldState(field) {
      return this.touched ? !!this.model[field] : null
    },
    addProfile() {
      this.model.social_profiles.push({ network: 'IG', handle: '', followers: null })
    },
    submit() {
      this.touched = true
      this.errorMessage = ''
      if (!this.canSubmit) return

      this.isSending = true
      const payload = {
        ...this.model,
        social_profiles: this.model.social_profiles.filter(p => (p.handle || '').trim())
      }

      axios.post(`${process.env.VUE_APP_API_BASE_URL}/api/v1/influencer_applications`, payload)
        .then(response => {
          this.sent = true
          this.sentMessage = response.data.message || this.$t('influencer_apply.sent_default')
        })
        .catch(error => { this.errorMessage = this.extractMessage(error) })
        .finally(() => { this.isSending = false })
    },
    /**
     * `messages` do backend é uma string com JSON dentro (convenção do
     * NotificationException) — precisa de dois parses.
     */
    extractMessage(error) {
      const data = error?.response?.data
      if (data?.messages) {
        try {
          const parsed = JSON.parse(data.messages)
          if (Array.isArray(parsed)) return parsed.join(' ')
        } catch (e) { return data.messages }
      }
      if (data?.errors) {
        return Object.values(data.errors).flat().join(' ')
      }
      return this.$t('influencer_apply.error')
    }
  }
}
</script>

<style scoped>
.apply-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  background: #f8f8f8;
}

.apply-card {
  width: 100%;
  max-width: 640px;
  background: #fff;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.w-full { width: 100%; }
</style>
